"use client";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import LoadingSpinner from "@/app/admin/_common/LoadingSpinner";
import "./CuoConfirmDialog.scss";
import { X as Close } from "lucide-react";

interface CuoConfirmDialogProps {
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  open: boolean;
  onClose: () => void;
  message: React.ReactNode;
  title: string;
  cancelText?: string;
  confirmText?: string;
  destructive?: boolean;
  icon?: React.ReactNode;
}

export default function CuoConfirmDialog(props: CuoConfirmDialogProps) {
  const {
    onConfirm,
    open,
    onClose,
    message,
    title,
    icon,
    cancelText,
    confirmText,
    onCancel,
    destructive,
  } = props;

  const [isConfirming, setIsConfirming] = useState(false);
  const isDestructive =
    destructive ?? title.toLowerCase().includes("delete");

  async function handleConfirm() {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsConfirming(false);
    }
  }

  function handleCancel() {
    onCancel?.();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="cuo-confirm-dialog"
      maxWidth="xs"
      fullWidth
    >
      <Box className="cuo-confirm-dialog__header">
        <Box className="cuo-confirm-dialog__title-row">
          {icon && (
            <Box
              className={`cuo-confirm-dialog__icon ${
                isDestructive ? "cuo-confirm-dialog__icon--destructive" : ""
              }`}
            >
              {icon}
            </Box>
          )}
          <Typography className="cuo-confirm-dialog__title">{title}</Typography>
        </Box>
        <IconButton
          size="small"
          disableRipple
          onClick={onClose}
          className="cuo-confirm-dialog__close"
          aria-label="Close"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box className="cuo-confirm-dialog__body">
        {typeof message === "string" ? (
          <Typography className="cuo-confirm-dialog__message">
            {message}
          </Typography>
        ) : (
          message
        )}
      </Box>

      <Box className="cuo-confirm-dialog__actions">
        <Button
          onClick={handleCancel}
          variant="outlined"
          disabled={isConfirming}
          className="cuo-confirm-dialog__button cuo-confirm-dialog__button--cancel"
        >
          {cancelText ?? "Cancel"}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disableElevation
          disabled={isConfirming}
          autoFocus
          className={`cuo-confirm-dialog__button cuo-confirm-dialog__button--confirm ${
            isDestructive ? "cuo-confirm-dialog__button--destructive" : ""
          }`}
        >
          {isConfirming ? (
            <LoadingSpinner showText={false} size={22} />
          ) : (
            confirmText ?? (isDestructive ? "Delete" : "Confirm")
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
